import { useEffect, useState } from "react";
import {
  HiArrowRight,
  HiOutlineCalendar,
  HiOutlineStar,
  HiOutlineVideoCamera,
} from "react-icons/hi";
import { Link } from "react-router-dom";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Card from "../components/Card";
import StarRating from "../components/StarRating";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";

export default function Home() {
  const { user } = useAuth();
  const [featuredTutors, setFeaturedTutors] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tutorsRes, statsRes] = await Promise.all([
        api.get("/tutors?limit=6"),
        api
          .get("/public/stats")
          .catch(() => ({ stats: { tutors: 0, sessions: 0, students: 0 } })),
      ]);
      setFeaturedTutors(tutorsRes.tutors || []);
      setStats(statsRes.stats || { tutors: 0, sessions: 0, students: 0 });
    } catch (error) {
      console.error("Error loading home data:", error);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "bg-primary/80",
      "bg-secondary/80",
      "bg-accent/80",
      "bg-primary/60",
      "bg-secondary/60",
      "bg-accent/60",
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  return (
    <div className="bg-background text-text">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/15"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-5xl font-extrabold text-text sm:text-6xl md:text-7xl">
              <span className="block">Learn Together,</span>
              <span className="block text-primary">Grow Together</span>
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-text/80">
              Connect with expert peer tutors, schedule personalized sessions,
              and achieve your academic goals with our comprehensive tutoring
              platform.
            </p>
            {!user && (
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-lg text-white bg-gradient-to-r from-primary to-secondary hover:shadow-lg transition-all transform hover:-translate-y-1"
                >
                  Get Started Free
                  <HiArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/tutors"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-lg text-primary bg-surface border border-border hover:bg-primary hover:text-white transition-all"
                >
                  Browse Tutors
                </Link>
              </div>
            )}
            {user && (
              <div className="mt-10">
                <Link
                  to={`/${user.role}/dashboard`}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-lg text-white bg-gradient-to-r from-primary to-secondary hover:shadow-lg transition-all"
                >
                  Go to Dashboard
                  <HiArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="bg-surface border-y border-border py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-primary">
                  {stats.tutors || 0}+
                </div>
                <div className="mt-2 text-lg text-text/70">Expert Tutors</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary">
                  {stats.sessions || 0}+
                </div>
                <div className="mt-2 text-lg text-text/70">
                  Sessions Completed
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary">
                  {stats.students || 0}+
                </div>
                <div className="mt-2 text-lg text-text/70">Happy Students</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      <div className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-text">
              Why Choose Our Platform?
            </h2>
            <p className="mt-4 text-lg text-text/70">
              Everything you need for successful peer tutoring
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 text-primary">
                <HiOutlineCalendar className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-text mb-2">
                Easy Scheduling
              </h3>
              <p className="text-text/70">
                Book sessions with your preferred tutors at times that work for
                you. Get instant confirmations via email and WhatsApp.
              </p>
            </Card>

            <Card>
              <div className="w-12 h-12 bg-secondary/15 rounded-lg flex items-center justify-center mb-4 text-secondary">
                <HiOutlineVideoCamera className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-text mb-2">
                Virtual Sessions
              </h3>
              <p className="text-text/70">
                Join sessions via Google Meet links sent automatically. Learn
                from anywhere, anytime.
              </p>
            </Card>

            <Card>
              <div className="w-12 h-12 bg-accent/15 rounded-lg flex items-center justify-center mb-4 text-accent">
                <HiOutlineStar className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-text mb-2">
                Rate & Review
              </h3>
              <p className="text-text/70">
                Share feedback after each session. Help build a trusted
                community of learners and tutors.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Featured Tutors */}
      {featuredTutors.length > 0 && (
        <div className="py-16 bg-surface border-y border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-text">
                Meet Our Top Tutors
              </h2>
              <p className="mt-4 text-lg text-text/70">
                Learn from the best peer tutors in your subjects
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredTutors.map((tutor) => (
                <Card key={tutor._id}>
                  <div className="flex items-center mb-4">
                    <div
                      className={`w-16 h-16 rounded-full ${getAvatarColor(
                        tutor.name?.first
                      )} flex items-center justify-center text-white text-xl font-bold`}
                    >
                      {getInitials(tutor.name?.first, tutor.name?.last)}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-text">
                        {tutor.name?.first} {tutor.name?.last}
                      </h3>
                      <div className="mt-1">
                        <StarRating
                          value={tutor.profile?.rating || 0}
                          readonly
                          size="sm"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-text/70 mb-4 line-clamp-2">
                    {tutor.profile?.bio ||
                      "Experienced tutor ready to help you succeed."}
                  </p>
                  {tutor.profile?.subjects &&
                    tutor.profile.subjects.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {tutor.profile.subjects.slice(0, 3).map((subj, idx) => (
                          <Badge
                            key={idx}
                            label={subj.subjectId?.name}
                            variant="primary"
                            size="small"
                            className="capitalize border border-primary/20 bg-primary/10 text-primary"
                          />
                        ))}
                      </div>
                    )}
                  <Link to={`/tutors/${tutor._id}`} className="block">
                    <Button className="w-full">View Profile</Button>
                  </Link>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/tutors"
                className="inline-flex items-center text-primary hover:text-primary/80 font-medium"
              >
                View All Tutors
                <HiArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      {!user && (
        <div className="bg-gradient-to-r from-primary to-secondary py-16">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Learning?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of students improving their grades with peer
              tutoring
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-lg text-primary bg-surface border border-border hover:bg-primary hover:text-white transition-all"
            >
              Sign Up Now - It's Free!
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
